import { ComponentType, ReactNode, Ref } from 'react';

import { styled, Typography } from '@mui/material';

import { cssVars } from '../../../utils/cssVars';
import Chip, { ChipProps } from '../Chip';

const { setValues } = cssVars({}, 'ds-chip');

export type StatusChipProps = ChipProps & {
  /** Only shown for medium and large sizes. */
  caption?: ReactNode;
  ref?: Ref<HTMLDivElement>;
};

const StatusChipBase = styled(Chip as ComponentType<StatusChipProps>, {
  target: 'StatusChip-root',
  shouldForwardProp: (prop) => prop !== 'hasCaption',
})<{ hasCaption: boolean }>(({ size, iconChip, hasCaption, theme }) => [
  size === 'sm' && {
    ...setValues({
      ...theme.typography.P10M,
      px: '8px',
      gap: '4px',
      height: '20px',
    }),
    '& .DsChip-IconWrapper': {
      fontSize: 12,
    },
    '& .MuiCircularProgress-root': {
      width: '12px !important',
      height: '12px !important',
    },
  },
  size === 'md' && {
    ...setValues({
      ...theme.typography.P12M,
      lineHeight: hasCaption ? 1 : theme.typography.P12M.lineHeight,
      px: '8px',
      gap: '6px',
      height: '28px',
    }),
    '& .DsChip-IconWrapper': {
      fontSize: 16,
    },
    '& .MuiCircularProgress-root': {
      width: '12px !important',
      height: '12px !important',
      marginRight: '4px',
    },
  },
  size === 'lg' && {
    ...setValues({
      ...theme.typography.P14M,
      lineHeight: hasCaption ? 1 : theme.typography.P14M.lineHeight,
      px: '10px',
      gap: '6px',
      height: '32px',
    }),
    '& .DsChip-IconWrapper': {
      fontSize: 20,
    },
    '& .MuiCircularProgress-root': {
      width: '16px !important',
      height: '16px !important',
      marginRight: '4px',
    },
  },
  iconChip && {
    ...setValues({ px: '0px' }),
  },
  hasCaption && {
    '& .DsChip-Content': {
      flexDirection: 'column',
      rowGap: '2px',
    },
  },
]);

const StatusChip = ({
  children,
  caption,
  size = 'md',
  ref,
  ...props
}: StatusChipProps) => {
  return (
    <StatusChipBase {...props} size={size} hasCaption={!!caption} ref={ref}>
      {children}
      {caption && size !== 'sm' && (
        <Typography
          className="StatusChip-Caption"
          variant="L10M"
          fontSize={8}
          lineHeight="1"
        >
          {caption}
        </Typography>
      )}
    </StatusChipBase>
  );
};

export default StatusChip;
